// Thin fetch wrapper: adds the session token, turns { ok:false, error } into ApiError, and logs
// out on 401.
import { useAuth } from '@/store/auth';
import i18n from '@/i18n';
import { humanize } from '@/lib/utils';

export class ApiError extends Error {
  constructor(public status: number, public code: string, public body: Record<string, unknown>) { super(code); }
}

type Query = Record<string, string | number | boolean | null | undefined>;

export function qs(q?: Query): string {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown; query?: Query; raw?: BodyInit; contentType?: string } = {}): Promise<T> {
  const token = useAuth.getState().token;
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  let body: BodyInit | undefined;
  if (opts.raw !== undefined) { body = opts.raw; if (opts.contentType) headers['content-type'] = opts.contentType; }
  else if (opts.body !== undefined) { body = JSON.stringify(opts.body); headers['content-type'] = 'application/json'; }
  const res = await fetch(`/api${path}${qs(opts.query)}`, { method: opts.method ?? (body ? 'POST' : 'GET'), headers, body });
  let data: any = null;
  const text = await res.text();
  try { data = text ? JSON.parse(text) : {}; } catch { data = { raw: text }; }
  if (res.status === 401 && token) useAuth.getState().logout();
  if (!res.ok || data?.ok === false) throw new ApiError(res.status, data?.error ?? `HTTP_${res.status}`, data ?? {});
  return data as T;
}

// download a protected file (CSV, recording) with the token, then hand it to the browser
export async function download(path: string, query?: Query, filename?: string): Promise<void> {
  const token = useAuth.getState().token;
  const res = await fetch(`/api${path}${qs(query)}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, `HTTP_${res.status}`, {});
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? path.split('/').pop() ?? 'download';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export async function blobUrl(path: string): Promise<string> {
  const token = useAuth.getState().token;
  const res = await fetch(`/api${path}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, `HTTP_${res.status}`, {});
  return URL.createObjectURL(await res.blob());
}

// an API or engine error code in the portal's language: err.CODE in the language files, else the code as words
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const f = e.body?.field ? ` (${i18n.t(humanize(String(e.body.field)))})` : '';
    const key = `err.${e.code}`;
    return `${i18n.exists(key) ? i18n.t(key) : e.code.replace(/_/g, ' ').toLowerCase()}${f}`;
  }
  return (e as Error)?.message ?? String(e);
}
